import React from "react";
import POSProductList from "../pages/POSProductList";
import CartSidebar from "../components/cart/CartSidebar";
import { POSProductsProvider } from "../contexts/POSProductContext";
import { CartProvider } from "../contexts/cartContext";

import { ShoppingCart } from "lucide-react";
import { useCart } from "../contexts/cartContext";

function Dashboard() {
    const { cart } = useCart();
    const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth < 768; // Tailwind's md breakpoint
        }
        return false;
    });
    const [mobileCartOpen, setMobileCartOpen] = React.useState(false);
    const [paginationElement, setPaginationElement] = React.useState(null);
    const [cartBottom, setCartBottom] = React.useState(24);

    React.useLayoutEffect(() => {
        if (!isMobile || !paginationElement) {
            setCartBottom(24);
            return undefined;
        }

        // Lift the cart only enough to clear the currently visible pagination.
        const updateCartBottom = () => {
            const bounds = paginationElement.getBoundingClientRect();
            const visible = bounds.height > 0 && bounds.top < window.innerHeight && bounds.bottom > 0;
            setCartBottom(visible ? Math.max(24, window.innerHeight - bounds.top + 8) : 24);
        };
        updateCartBottom();
        const observer = new ResizeObserver(updateCartBottom);
        observer.observe(paginationElement);
        window.addEventListener('resize', updateCartBottom);
        window.addEventListener('scroll', updateCartBottom, true);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateCartBottom);
            window.removeEventListener('scroll', updateCartBottom, true);
        };
    }, [isMobile, paginationElement]);

    React.useEffect(() => {
        const handleResize = () => {
            const nextIsMobile = window.innerWidth < 768;
            setIsMobile(nextIsMobile);
            if (!nextIsMobile) setMobileCartOpen(false);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <POSProductsProvider>
            <CartProvider>
                <div className="flex flex-1 gap-6 overflow-hidden relative">
                    <main className="flex-1 overflow-y-auto scrollbar-thin">
                        <POSProductList sidebarCollapsed={isMobile && !mobileCartOpen} onPaginationElementChange={setPaginationElement} />
                    </main>
                    {/* Sidebar toggle button removed; close handled inside CartSidebar */}
                    {/* On small screens, floating sidebar overlays the grid. On md and up, sidebar is part of the flex layout. */}
                    {/* Small screens: floating sidebar overlays the grid. */}
                    {/* Floating cart button for small screens when sidebar is hidden */}
                    {isMobile && !mobileCartOpen && (
                        <button
                            className="fixed right-6 z-40 bg-primary text-white rounded-full p-4 shadow-lg md:hidden"
                            style={{ bottom: cartBottom }}
                            onClick={() => setMobileCartOpen(true)}
                            aria-label="Show cart sidebar"
                            type="button"
                        >
                            <span className="sr-only">Show cart</span>
                            <span className="flex items-center justify-center relative">
                                <ShoppingCart
                                    size={28}
                                    className="text-white"
                                />
                                {/* Cart counter badge */}
                                {cart && cart.length > 0 && (
                                    <span
                                        className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 shadow"
                                        style={{ minWidth: 20, textAlign: 'center' }}
                                    >
                                        {cart.reduce((sum, i) => sum + i.qty, 0)}
                                    </span>
                                )}
                            </span>
                        </button>
                    )}
                    {isMobile && mobileCartOpen && (
                        <>
                            <aside
                                className="fixed top-20 bottom-4 right-0 flex w-[min(92vw,360px)] flex-col overflow-x-hidden rounded-l-xl bg-white shadow-2xl transition-all duration-300 z-50 md:hidden"
                            >
                                <CartSidebar onCloseSidebar={() => setMobileCartOpen(false)} />
                            </aside>
                            <div
                                className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
                                onClick={() => setMobileCartOpen(false)}
                                aria-label="Close cart sidebar overlay"
                            />
                        </>
                    )}
                    {/* md and up: sidebar in flex layout */}
                    {!isMobile && (
                        <aside className="hidden shrink-0 flex-col overflow-x-hidden rounded-xl transition-all duration-300 md:flex md:w-[340px] xl:w-[360px] 2xl:w-[380px]">
                            <CartSidebar />
                        </aside>
                    )}
                </div>
            </CartProvider>
        </POSProductsProvider>
    );
}

export default Dashboard;
